import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const ConfirmReschedule: React.FC = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [oldDate, setOldDate] = useState('');
  const [oldTime, setOldTime] = useState('');
  const [showNewDateSelection, setShowNewDateSelection] = useState(false);

  // Get email from URL parameters if provided
  const emailParam = searchParams.get('email');

  const content = {
    en: {
      title: "Confirm Appointment Reschedule",
      subtitle: "Please provide your current appointment details",
      form: {
        email: "Email Address",
        emailPlaceholder: "Enter your email address",
        oldDate: "Current Appointment Date",
        oldTime: "Current Appointment Time",
        next: "Next"
      },
      error: {
        required: "All fields are required",
        invalidEmail: "Please enter a valid email address",
        generic: "Something went wrong. Please try again."
      }
    },
    da: {
      title: "Bekræft Ombookning af Aftale",
      subtitle: "Angiv venligst dine nuværende aftaledetaljer",
      form: {
        email: "E-mailadresse",
        emailPlaceholder: "Indtast din e-mailadresse",
        oldDate: "Nuværende Aftaledato",
        oldTime: "Nuværende Aftaletid",
        next: "Næste"
      },
      error: {
        required: "Alle felter skal udfyldes",
        invalidEmail: "Indtast venligst en gyldig e-mailadresse",
        generic: "Noget gik galt. Prøv venligst igen."
      }
    }
  };

  useEffect(() => {
    if (emailParam) {
      setEmail(emailParam);
    }
  }, [emailParam]);

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleNext = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim() || !oldDate || !oldTime) {
      setError(content[language as 'en' | 'da'].error.required);
      return;
    }

    if (!validateEmail(email.trim())) {
      setError(content[language as 'en' | 'da'].error.invalidEmail);
      return;
    }

    // Force a complete page refresh by using window.location
    const newUrl = `/reschedule-new-date?email=${encodeURIComponent(email)}&old_date=${oldDate}&old_time=${oldTime}&t=${Date.now()}`;
    window.location.href = newUrl;
  };

  return (
    <div className="min-h-screen bg-[#1A1A1A] pt-32 pb-20">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <h1
              className="text-3xl md:text-4xl font-serif font-bold mb-4 text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {content[language as 'en' | 'da'].title}
            </h1>
            <p className="text-lg text-gray-400">
              {content[language as 'en' | 'da'].subtitle}
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 mb-8">
            <form onSubmit={handleNext} className="space-y-6">
              {error && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <p className="text-red-500 text-sm">{error}</p>
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                  {content[language as 'en' | 'da'].form.email}
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={content[language as 'en' | 'da'].form.emailPlaceholder}
                  className="w-full px-4 py-3 rounded-lg bg-[#2A2A2A]/50 border border-white/10 text-gray-400 placeholder-gray-500 cursor-not-allowed opacity-60"
                  required
                  disabled
                  readOnly
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="oldDate" className="block text-sm font-medium text-white mb-2">
                    {content[language as 'en' | 'da'].form.oldDate}
                  </label>
                  <input
                    type="date"
                    id="oldDate"
                    value={oldDate}
                    onChange={(e) => setOldDate(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-[#2A2A2A] border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-[#A68B7C]/50 focus:border-[#A68B7C]"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="oldTime" className="block text-sm font-medium text-white mb-2">
                    {content[language as 'en' | 'da'].form.oldTime}
                  </label>
                  <input
                    type="time"
                    id="oldTime"
                    value={oldTime}
                    onChange={(e) => setOldTime(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-[#2A2A2A] border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-[#A68B7C]/50 focus:border-[#A68B7C]"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-6 py-3 bg-[#A68B7C] text-white font-medium rounded-lg hover:bg-[#95796A] transition-all duration-300 disabled:opacity-70 flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Processing...
                  </>
                ) : content[language as 'en' | 'da'].form.next}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmReschedule;